import React, { useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Users2, Activity, Clock, ChevronRight } from "lucide-react";

/**
 * Resumen por equipos para el Director Deportivo. Se muestra cuando está
 * seleccionado "Todos los equipos": evita descargar visualmente cientos de
 * jugadores de una vez. Al pulsar un equipo se carga su dashboard concreto.
 */
export default function TeamsSummary({ teams, rows, onSelect }) {
  const byTeam = useMemo(() => {
    const m = {};
    (rows || []).forEach((r) => {
      const t = (m[r.team_id] = m[r.team_id] || { count: 0, minutes: 0, attSum: 0, attN: 0 });
      t.count += 1;
      t.minutes += r.minutes || 0;
      if (typeof r.attendance_pct === "number") { t.attSum += r.attendance_pct; t.attN += 1; }
    });
    return m;
  }, [rows]);

  const cards = (teams || []).map((t) => {
    const agg = byTeam[t.id] || { count: 0, minutes: 0, attSum: 0, attN: 0 };
    return {
      id: t.id, name: t.name, category: t.category,
      count: agg.count, minutes: agg.minutes,
      attendance: agg.attN ? Math.round(agg.attSum / agg.attN) : null,
    };
  });

  return (
    <div data-testid="teams-summary">
      <h2 className="text-xl font-black mb-3" style={{ fontFamily: "Nunito" }}>Resumen por equipos</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c) => (
          <Card
            key={c.id}
            role="button"
            onClick={() => onSelect(c.id)}
            data-testid={`team-summary-${c.id}`}
            className="border-slate-200 shadow-sm cursor-pointer hover:-translate-y-1 hover:shadow-md transition-transform group"
          >
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-lg font-black leading-tight truncate group-hover:text-purple-700 transition-colors"
                    style={{ fontFamily: "Nunito" }} title={c.name}>
                    {c.name}
                  </div>
                  {c.category && (
                    <div className="mt-1 inline-flex px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 text-[10px] font-bold uppercase tracking-wide">
                      {c.category}
                    </div>
                  )}
                </div>
                <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-purple-600 transition-colors shrink-0" />
              </div>
              <div className="grid grid-cols-3 gap-2 mt-4">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-slate-400"><Users2 className="h-3.5 w-3.5" /></div>
                  <div className="text-xl font-black" style={{ fontFamily: "Nunito" }}>{c.count}</div>
                  <div className="text-[9px] uppercase tracking-widest text-slate-400">Jugadores</div>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-slate-400"><Activity className="h-3.5 w-3.5" /></div>
                  <div className="text-xl font-black" style={{ fontFamily: "Nunito" }}>{c.attendance != null ? `${c.attendance}%` : "—"}</div>
                  <div className="text-[9px] uppercase tracking-widest text-slate-400">Asist.</div>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-slate-400"><Clock className="h-3.5 w-3.5" /></div>
                  <div className="text-xl font-black" style={{ fontFamily: "Nunito" }}>{c.minutes}</div>
                  <div className="text-[9px] uppercase tracking-widest text-slate-400">Minutos</div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {cards.length === 0 && <div className="text-sm text-slate-500">No hay equipos registrados.</div>}
      </div>
    </div>
  );
}
